/**
 * React Query hooks for Employee Payments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeePaymentsApi } from '../lib/api';

export const paymentKeys = {
  all: ['payments'],
  employee: (id) => [...paymentKeys.all, 'employee', id],
  stats: (id, filters) => [...paymentKeys.all, 'stats', id, filters],
  unpaid: () => [...paymentKeys.all, 'unpaid'],
};

// Get payments for an employee
export function useEmployeePayments(employeeId) {
  return useQuery({
    queryKey: paymentKeys.employee(employeeId),
    queryFn: () => employeePaymentsApi.getByEmployee(employeeId),
    enabled: !!employeeId,
    staleTime: 2 * 60 * 1000,
  });
}

// Get payment stats for an employee
export function useEmployeePaymentStats(employeeId, filters = {}) {
  return useQuery({
    queryKey: paymentKeys.stats(employeeId, filters),
    queryFn: () => employeePaymentsApi.getStats(employeeId, filters),
    enabled: !!employeeId,
  });
}

// Get unpaid weeks
export function useUnpaidWeeks() {
  return useQuery({
    queryKey: paymentKeys.unpaid(),
    queryFn: () => employeePaymentsApi.getUnpaid(),
    staleTime: 1 * 60 * 1000,
  });
}

// Get cumulative payment data for a week
export function useCumulativePayments(weekStart) {
  return useQuery({
    queryKey: [...paymentKeys.all, 'cumulative', weekStart],
    queryFn: () => employeePaymentsApi.getCumulative(weekStart),
    enabled: !!weekStart,
    staleTime: 1 * 60 * 1000,
  });
}

// Create payment mutation
export function useCreatePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: employeePaymentsApi.create,
    onSuccess: (data) => {
      // Invalidate employee payments and stats
      queryClient.invalidateQueries({ queryKey: paymentKeys.employee(data.employee_id) });
      queryClient.invalidateQueries({ queryKey: paymentKeys.unpaid() });
      // Invalidate cumulative queries για να ενημερωθεί η σελίδα Πληρωμών v2
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'cumulative'] });
      // Invalidate salaries queries
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
    },
  });
}

// Update payment mutation
export function useUpdatePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => employeePaymentsApi.update(id, data),
    onSuccess: (data, variables) => {
      console.log('✅ Update payment mutation success:', data);
      // Use employee_id from response or from variables
      const employeeId = data?.employee_id || variables?.data?.employee_id;
      if (employeeId) {
        queryClient.invalidateQueries({ queryKey: paymentKeys.employee(employeeId) });
      }
      // Invalidate all payment queries to be safe
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentKeys.unpaid() });
      // Invalidate cumulative queries για να ενημερωθεί η σελίδα Πληρωμών v2
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'cumulative'] });
      // Invalidate salaries queries
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
    },
    onError: (error) => {
      console.error('❌ Update payment mutation error:', error);
    },
  });
}

// Delete payment mutation
export function useDeletePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: employeePaymentsApi.delete,
    onSuccess: (data) => {
      console.log('✅ Delete payment mutation success:', data);
      // Invalidate all payment queries
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentKeys.unpaid() });
      // If we have employee_id, invalidate that specific query too
      if (data?.employee_id) {
        queryClient.invalidateQueries({ queryKey: paymentKeys.employee(data.employee_id) });
      }
      // Invalidate cumulative queries για να ενημερωθεί η σελίδα Πληρωμών v2
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'cumulative'] });
      // Invalidate salaries queries
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
    },
    onError: (error) => {
      console.error('❌ Delete payment mutation error:', error);
    },
  });
}

